import React, { useState, useEffect } from "react";
import { Lock, Eye, EyeOff, Check, X, AlertTriangle, Shield } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Password strength checker
const getPasswordStrength = (pwd) => {
  if (!pwd) return { score: 0, label: "", color: "" };
  let score = 0;
  if (pwd.length >= 8) score++;
  if (pwd.length >= 12) score++;
  if (/[A-Z]/.test(pwd)) score++;
  if (/[a-z]/.test(pwd)) score++;
  if (/\d/.test(pwd)) score++;
  if (/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) score++;

  if (score <= 2) return { score, label: "Weak", color: "bg-red-500" };
  if (score <= 4) return { score, label: "Fair", color: "bg-amber-500" };
  if (score <= 5) return { score, label: "Strong", color: "bg-green-500" };
  return { score, label: "Very Strong", color: "bg-emerald-600" };
};

const policyChecks = (pwd) => [
  { label: "At least 8 characters", met: pwd.length >= 8 },
  { label: "Uppercase letter (A-Z)", met: /[A-Z]/.test(pwd) },
  { label: "Lowercase letter (a-z)", met: /[a-z]/.test(pwd) },
  { label: "Number (0-9)", met: /\d/.test(pwd) },
  { label: "Special character (!@#$%^&*)", met: /[!@#$%^&*(),.?":{}|<>]/.test(pwd) },
];

export const PasswordChangeModal = ({ auth, isForced, onSuccess, onCancel }) => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const strength = getPasswordStrength(newPassword);
  const checks = policyChecks(newPassword);
  const allChecksMet = checks.every(c => c.met);
  const passwordsMatch = newPassword && newPassword === confirmPassword;
  const totpValid = !twoFactorEnabled || /^\d{6}$/.test(totpCode);

  // Detect 2FA on mount so we can show the TOTP field only when needed.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = auth?.token || "";
        const res = await fetch(`${API_URL}/api/admin/2fa/status`, {
          credentials: "include",
          headers: token ? { "X-Admin-Auth": token } : {},
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setTwoFactorEnabled(!!data?.enabled);
      } catch (e) { /* non-fatal — assume 2FA off */ }
    })();
    return () => { cancelled = true; };
  }, [auth]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!allChecksMet) {
      setError("Password does not meet all requirements");
      return;
    }
    if (!passwordsMatch) {
      setError("Passwords do not match");
      return;
    }
    if (twoFactorEnabled && !/^\d{6}$/.test(totpCode)) {
      setError("Enter the 6-digit code from your Authenticator app");
      return;
    }

    setLoading(true);
    try {
      const token = auth?.token || "";
      const res = await fetch(`${API_URL}/api/auth/change-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          // Defense-in-depth: pass the JWT explicitly in addition to the
          // HttpOnly cookie. If cookies are blocked (private tab, iOS
          // third-party cookie settings, some corporate proxies), the
          // header keeps the request authenticated.
          ...(token ? { "X-Admin-Auth": token } : {}),
        },
        credentials: "include",
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
          ...(twoFactorEnabled ? { totp_code: totpCode } : {}),
        }),
      });
      let data = {};
      try { data = await res.json(); } catch { /* response had no JSON body */ }
      if (!res.ok) {
        // Server may signal "TOTP_REQUIRED" when 2FA was enabled mid-session;
        // flip the flag and ask for the code in-place instead of erroring out.
        if (res.status === 403 && (data.detail || "").startsWith("TOTP_REQUIRED")) {
          setTwoFactorEnabled(true);
          setError("Enter the 6-digit code from your Authenticator app to confirm this change.");
          return;
        }
        // Show the backend's message if provided; otherwise an HTTP-specific
        // message so we never hide the failure mode behind "Network error".
        const fallback = res.status === 401
          ? "Your session expired. Please log in again."
          : res.status >= 500
          ? "Server error. Please try again in a moment."
          : `Request failed (HTTP ${res.status})`;
        setError(data.detail || fallback);
        console.error("[change-password] failed:", res.status, data);
        return;
      }
      onSuccess(data.token);
    } catch (err) {
      // Real network failure (CORS, DNS, offline). Surface the actual error.
      console.error("[change-password] network error:", err);
      setError(`Network error: ${err?.message || "unknown"}. Please check your connection and try again.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="password-change-modal">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="text-center mb-6">
          <div className={`w-16 h-16 ${isForced ? "bg-red-100" : "bg-blue-100"} rounded-full flex items-center justify-center mx-auto mb-4`}>
            {isForced ? <AlertTriangle size={32} className="text-red-600" /> : <Shield size={32} className="text-blue-600" />}
          </div>
          <h2 className="text-xl font-bold text-[#1E1B4B]">
            {isForced ? "Password Change Required" : "Change Password"}
          </h2>
          {isForced && (
            <p className="text-[#475569] mt-2 text-sm">
              Your password has expired or this is your first login. Please set a new password to continue.
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Current Password */}
          <div>
            <label className="block text-sm font-medium text-[#1E1B4B] mb-1.5">Current Password</label>
            <div className="relative">
              <input
                type={showCurrent ? "text" : "password"}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#E83E8C] focus:ring-2 focus:ring-[#E83E8C]/20 outline-none pr-12"
                placeholder="Enter current password"
                required
                data-testid="current-password-input"
              />
              <button
                type="button"
                onClick={() => setShowCurrent(!showCurrent)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showCurrent ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {/* New Password */}
          <div>
            <label className="block text-sm font-medium text-[#1E1B4B] mb-1.5">New Password</label>
            <div className="relative">
              <input
                type={showNew ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#E83E8C] focus:ring-2 focus:ring-[#E83E8C]/20 outline-none pr-12"
                placeholder="Enter new password"
                required
                data-testid="new-password-input"
              />
              <button
                type="button"
                onClick={() => setShowNew(!showNew)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showNew ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            {/* Strength Bar */}
            {newPassword && (
              <div className="mt-2">
                <div className="flex gap-1 mb-1">
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div
                      key={i}
                      className={`h-1.5 flex-1 rounded-full transition-colors ${i <= strength.score ? strength.color : "bg-gray-200"}`}
                    />
                  ))}
                </div>
                <span className={`text-xs font-medium ${strength.color.replace("bg-", "text-")}`}>{strength.label}</span>
              </div>
            )}
          </div>

          {/* Policy Checklist */}
          {newPassword && (
            <div className="bg-gray-50 rounded-xl p-4 space-y-2" data-testid="password-policy-checklist">
              {checks.map((c, i) => (
                <div key={c.label} className="flex items-center gap-2 text-sm">
                  {c.met ? (
                    <Check size={14} className="text-green-500 shrink-0" />
                  ) : (
                    <X size={14} className="text-red-400 shrink-0" />
                  )}
                  <span className={c.met ? "text-green-700" : "text-gray-500"}>{c.label}</span>
                </div>
              ))}
            </div>
          )}

          {/* Confirm Password */}
          <div>
            <label className="block text-sm font-medium text-[#1E1B4B] mb-1.5">Confirm New Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className={`w-full px-4 py-3 rounded-xl border ${confirmPassword && !passwordsMatch ? "border-red-300 focus:border-red-400" : "border-gray-200 focus:border-[#E83E8C]"} focus:ring-2 focus:ring-[#E83E8C]/20 outline-none`}
              placeholder="Confirm new password"
              required
              data-testid="confirm-password-input"
            />
            {confirmPassword && !passwordsMatch && (
              <p className="text-red-500 text-xs mt-1">Passwords do not match</p>
            )}
          </div>

          {/* 2FA TOTP — only shown when the user has Authenticator-app 2FA on */}
          {twoFactorEnabled && (
            <div data-testid="totp-field-wrapper">
              <label className="block text-sm font-medium text-[#1E1B4B] mb-1.5 flex items-center gap-1.5">
                <Shield size={14} className="text-emerald-600" />
                2FA Code (Authenticator app)
              </label>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className="w-full px-4 py-3 rounded-xl border border-emerald-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none tracking-[0.4em] text-center font-mono text-lg"
                placeholder="000000"
                maxLength={6}
                required
                data-testid="totp-code-input"
              />
              <p className="text-[11px] text-emerald-700/80 mt-1">
                Open Google Authenticator / Authy and enter the 6-digit code shown for DoozyGo Admin.
              </p>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="bg-red-50 text-red-600 px-4 py-3 rounded-xl text-sm" data-testid="password-change-error">
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={loading || !allChecksMet || !passwordsMatch || !totpValid}
              className="flex-1 bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C] text-white py-3 rounded-xl font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="change-password-submit-btn"
            >
              <Lock size={18} />
              {loading ? "Changing..." : "Change Password"}
            </button>
            {!isForced && (
              <button
                type="button"
                onClick={onCancel}
                className="px-6 py-3 bg-gray-100 text-[#475569] rounded-xl font-semibold hover:bg-gray-200 transition-colors"
                data-testid="change-password-cancel-btn"
              >
                Cancel
              </button>
            )}
          </div>

          {isForced && (
            <p className="text-center text-xs text-gray-400 mt-2">
              You must change your password to access the dashboard
            </p>
          )}
        </form>
      </div>
    </div>
  );
};

export default PasswordChangeModal;
